/* eslint-disable jsx-a11y/media-has-caption */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { AspectRatio, Box } from '@chakra-ui/react';
import { ConsentGatedYouTubeEmbed } from 'components/common/ConsentGatedYouTube';
import { CustomPlayButton } from 'components/common/VideoBox/CustomPlayButton';
import { ModalVideoVariantProps, SharedModalProps } from 'types/Modal';
import { logBrazeEvent } from 'utils/brazeEvents';
import { CustomModalContent } from '../CustomModalContent';
import { CustomModal } from '../CustomModal';

type Props = SharedModalProps & ModalVideoVariantProps;

export const VideoModal = ({
  videoUrl,
  videoDescription = '',
  isShown,
  handleClose,
  isEmbedded = false,
  onVideoEnd,
  onVideoAbort,
}: Props): ReactElement => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [shouldShowPlayButton, setShouldShowPlayButton] = useState<boolean>(!isEmbedded);
  const [isVideoWatchedTracked, setIsVideoWatchedTracked] = useState(false);

  const handlePlayClick = (): void => {
    if (isEmbedded) return;

    if (videoRef.current?.paused || videoRef.current?.ended) {
      videoRef.current?.play();
      setShouldShowPlayButton(false);
      if (!isVideoWatchedTracked) {
        setIsVideoWatchedTracked(true);
        logBrazeEvent('video_watched', {
          videoUrl,
          videoDescription,
          isEmbedded,
        });
      }
    } else {
      videoRef.current?.pause();
      setShouldShowPlayButton(true);
    }
  };

  useEffect(() => {
    if (isEmbedded) return;

    const videoInstance = videoRef.current;
    if (videoInstance) {
      videoInstance.addEventListener('ended', () => {
        setShouldShowPlayButton(true);
      });
    }
    return () => {
      if (videoInstance) {
        videoInstance?.removeEventListener('ended', () => {
          setShouldShowPlayButton(true);
        });
      }
    };
  }, [videoRef, isEmbedded]);

  const onClose = (): void => {
    if (
      videoRef.current &&
      videoRef.current.currentTime < videoRef.current.duration &&
      videoRef.current.currentTime > 0 &&
      onVideoAbort
    ) {
      onVideoAbort();
    }
    handleClose();
  };

  return (
    <CustomModal isOpen={isShown} onClose={onClose} placement="center" size="lg">
      <CustomModalContent
        bg="background.quaternary"
        borderRadius="0%"
        color="text.onAccent"
        overflow="none"
      >
        <Box display="grid" placeItems="center">
          <Box position="relative" width="100%" onClick={handlePlayClick}>
            {shouldShowPlayButton && (
              <Box
                height="100%"
                width="100%"
                position="absolute"
                top="0"
                left="0"
                display="grid"
                placeItems="center"
              >
                <CustomPlayButton />
              </Box>
            )}
            <Box width="100%" overflow="hidden" bg={isEmbedded ? 'background.canvas' : undefined}>
              <AspectRatio maxW="100%" ratio={16 / 9} width="100%">
                {isEmbedded && videoUrl ? (
                  <ConsentGatedYouTubeEmbed embedUrl={videoUrl} title={videoDescription} />
                ) : (
                  <video title="test" src={videoUrl} ref={videoRef} controls onEnded={onVideoEnd}>
                    <source src={videoUrl} type="video/mp4" />
                  </video>
                )}
              </AspectRatio>
            </Box>
          </Box>
        </Box>
      </CustomModalContent>
    </CustomModal>
  );
};
